import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import clientService from '../services/clientService';
import toast from 'react-hot-toast';
import {
  HiOutlinePlus,
  HiOutlineSearch,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineUserGroup,
  HiOutlineX,
  HiOutlinePhone,
  HiOutlineLocationMarker,
} from 'react-icons/hi';
import './Clients.css';

const Clients = () => {
  const { hasRole } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [form, setForm] = useState({
    name: '', business_name: '', rnc: '', email: '',
    phone: '', address: '', credit_enabled: false,
    credit_limit: '', ncf_type_default: 'B02', client_type: 'minorista',
  });
  const [saving, setSaving] = useState(false);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const data = await clientService.getAll();
      setClients(data.data || data.clients || []);
    } catch {
      toast.error('Error cargando clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, []);

  const filtered = clients.filter((c) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (c.contact_name || c.name || '').toLowerCase().includes(q) ||
      (c.company_name || c.business_name || '').toLowerCase().includes(q) ||
      c.rnc?.includes(q) ||
      c.phone?.includes(q)
    );
  });

  const openCreate = () => {
    setEditClient(null);
    setForm({ name: '', business_name: '', rnc: '', email: '', phone: '', address: '', credit_enabled: false, credit_limit: '', ncf_type_default: 'B02', client_type: 'minorista' });
    setShowModal(true);
  };

  const openEdit = (client) => {
    setEditClient(client);
    setForm({
      name: client.contact_name || client.name || '',
      business_name: client.company_name || client.business_name || '',
      rnc: client.rnc || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      credit_enabled: client.credit_enabled || false,
      credit_limit: client.credit_limit || '',
      ncf_type_default: client.ncf_type_default || 'B02',
      client_type: client.client_type || 'minorista',
    });
    setShowModal(true);
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`¿Eliminar cliente "${client.contact_name || client.name}"?`)) return;
    try {
      await clientService.delete(client.id);
      toast.success('Cliente eliminado');
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error eliminando');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Nombre requerido'); return; }
    setSaving(true);
    try {
      const payload = {
        contact_name: form.name,
        company_name: form.business_name || undefined,
        rnc: form.rnc || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        address: form.address || undefined,
        credit_enabled: form.credit_enabled,
        credit_limit: form.credit_limit ? Number(form.credit_limit) : undefined,
        ncf_type_default: form.ncf_type_default || 'B02',
        client_type: form.client_type || 'minorista',
      };
      if (editClient) {
        await clientService.update(editClient.id, payload);
        toast.success('Cliente actualizado');
      } else {
        await clientService.create(payload);
        toast.success('Cliente creado');
      }
      setShowModal(false);
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="clients-page">
      <div className="page-header">
        <div>
          <h1>Clientes</h1>
          <p>{clients.length} clientes registrados</p>
        </div>
        {hasRole('admin', 'manager', 'salesman') && (
          <button className="btn-primary" onClick={openCreate}>
            <HiOutlinePlus /> Nuevo Cliente
          </button>
        )}
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <HiOutlineSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por nombre, RNC, teléfono..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading-state"><div className="loader"></div><p>Cargando clientes...</p></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state-card">
          <HiOutlineUserGroup className="empty-icon" />
          <p>No se encontraron clientes</p>
        </div>
      ) : (
        <div className="clients-grid">
          {filtered.map((client) => (
            <div key={client.id} className="client-card">
              <div className="client-header">
                <div className="client-avatar">
                  {(client.contact_name || client.name || 'C').charAt(0).toUpperCase()}
                </div>
                <div className="client-meta">
                  <h4>{client.contact_name || client.name}</h4>
                  {(client.company_name || client.business_name) && <span className="client-business">{client.company_name || client.business_name}</span>}
                </div>
                <div className="client-actions">
                  {hasRole('admin', 'manager') && (
                    <>
                      <button className="btn-edit" onClick={() => openEdit(client)}><HiOutlinePencil /></button>
                      {hasRole('admin') && (
                        <button className="btn-remove" onClick={() => handleDelete(client)}><HiOutlineTrash /></button>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="client-details">
                {client.rnc && (
                  <div className="client-detail-row">
                    <span className="detail-label">RNC</span>
                    <span className="detail-value mono">{client.rnc}</span>
                  </div>
                )}
                {client.phone && (
                  <div className="client-detail-row">
                    <HiOutlinePhone className="detail-icon" />
                    <span className="detail-value">{client.phone}</span>
                  </div>
                )}
                {client.address && (
                  <div className="client-detail-row">
                    <HiOutlineLocationMarker className="detail-icon" />
                    <span className="detail-value truncate">{client.address}</span>
                  </div>
                )}
                <div className="client-badges">
                  <span className={`credit-badge ${client.client_type === 'mayorista' ? 'enabled' : 'info'}`}>
                    {client.client_type === 'mayorista' ? '🏢 Mayorista' : '🛒 Minorista'}
                  </span>
                  <span className={`credit-badge ${client.credit_enabled ? 'enabled' : 'disabled'}`}>
                    {client.credit_enabled ? 'Crédito habilitado' : 'Solo contado'}
                  </span>
                  <span className="ncf-badge">NCF: {client.ncf_type_default || 'B02'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editClient ? 'Editar Cliente' : 'Nuevo Cliente'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><HiOutlineX /></button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-grid-2">
                <div className="form-group">
                  <label>Nombre *</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Nombre Comercial</label>
                  <input type="text" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
                </div>
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label>RNC / Cédula</label>
                  <input type="text" value={form.rnc} onChange={(e) => setForm({ ...form, rnc: e.target.value })} placeholder="9 u 11 dígitos" />
                </div>
                <div className="form-group">
                  <label>Teléfono</label>
                  <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Dirección</label>
                <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} />
              </div>
              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.credit_enabled}
                    onChange={(e) => setForm({ ...form, credit_enabled: e.target.checked })}
                  />
                  <span>Habilitar crédito</span>
                </label>
              </div>
              {form.credit_enabled && (
                <div className="form-group">
                  <label>Límite de Crédito (RD$)</label>
                  <input type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} />
                </div>
              )}
              <div className="form-grid-2">
                <div className="form-group">
                  <label>Perfil de Cliente</label>
                  <select value={form.client_type} onChange={(e) => setForm({ ...form, client_type: e.target.value })}>
                    <option value="minorista">🛒 Minorista</option>
                    <option value="mayorista">🏢 Mayorista</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Tipo NCF Default</label>
                  <select value={form.ncf_type_default} onChange={(e) => setForm({ ...form, ncf_type_default: e.target.value })}>
                    <option value="B01">B01 - Crédito Fiscal</option>
                    <option value="B02">B02 - Consumo</option>
                    <option value="B14">B14 - Régimen Especial</option>
                    <option value="B15">B15 - Gubernamental</option>
                  </select>
                </div>
              </div>
              <button type="submit" className="btn-primary full" disabled={saving}>
                {saving ? 'Guardando...' : editClient ? 'Actualizar' : 'Crear Cliente'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
